import { Spinner } from "@carbon6/c6-frontend-design";
import { useSyncExternalStore, type ReactNode } from "react";
import type { TrainingActiveCounts } from "@/api/training";
import { HatTextTooltip } from "@/components/tooltip";
import { trainingActivityController } from "./trainingActivityController";
import styles from "./ActivityCountBadge.module.css";

// 배지가 셀 축이다. 사이드바 Training·Evaluation·Schedule 세 항목이 같은 스냅샷의 다른 칸을 읽는다.
export type ActivityAxis = "training" | "evaluation" | "schedule";

const AXIS_MESSAGE: Record<ActivityAxis, (count: number) => string> = {
  training: (count) => `진행 중인 Training이 ${count}개 있습니다.`,
  evaluation: (count) => `진행 중인 Evaluation 실행이 ${count}개 있습니다.`,
  schedule: (count) => `Schedule로 실행 중인 Training이 ${count}개 있습니다.`,
};

// 축 → 스냅샷 칸.
const AXIS_COUNT: Record<ActivityAxis, (counts: TrainingActiveCounts) => number> = {
  training: (counts) => counts.training,
  evaluation: (counts) => counts.evaluation,
  schedule: (counts) => counts.scheduled_sessions,
};

type ActivityCountBadgeProps = {
  axis: ActivityAxis;
  // 펼친 상태는 라벨 오른쪽 스피너 + 개수 행이다. 접힘 레일은 항목 아이콘 위 개수 배지다.
  // 접힘 여부는 사이드바를 소유한 PageBase 가 전달한다(GpuCapacityWidget 과 같은 계약).
  collapsed: boolean;
};

// 사이드바 항목 옆의 진행 표시다. 실행 중인 내 작업 수를 모든 페이지에서 보여 준다.
// 데이터는 모듈 싱글턴 컨트롤러가 소유한다. 그래서 페이지를 옮겨도 폴링과 직전 값이 유지된다.
export function ActivityCountBadge({ axis, collapsed }: ActivityCountBadgeProps): ReactNode {
  const { data: counts } = useSyncExternalStore(
    trainingActivityController.subscribe,
    trainingActivityController.getSnapshot,
    trainingActivityController.getSnapshot,
  );
  const count = counts === null ? null : AXIS_COUNT[axis](counts);

  // 진행 중인 작업이 없거나 아직 모르면 아무것도 그리지 않는다. 평시 사이드바는 지금 모양을
  // 유지해야 한다. 첫 로드와 조회 실패는 사용자가 조치할 일이 아니다.
  if (count === null || count === 0) {
    return null;
  }

  const message = AXIS_MESSAGE[axis](count);

  // 접힘 레일에서는 라벨이 숨으므로 개수만 표시한다. 항목(NavItem)이 이미 자기 툴팁을 가진다.
  // 여기서 툴팁을 추가하면 툴팁이 중첩된다. 그래서 같은 문장은 라이브 리전 이름으로만 전달한다.
  if (collapsed) {
    return (
      <span className={styles.railCount} role="status" aria-label={message}>
        <span aria-hidden="true">{count}</span>
      </span>
    );
  }

  return (
    <HatTextTooltip text={message} placement="right">
      <span className={styles.badge}>
        <Spinner size="sm" label={message} />
        <span className={styles.count} aria-hidden="true">
          {count}
        </span>
      </span>
    </HatTextTooltip>
  );
}
